import { createContext, useContext, useEffect, useState } from 'react';
import type {
  Theme,
  ThemeProviderProps,
  ThemeProviderState,
} from '@/types/theme.types';

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Provides a theme context to its children.
 *
 * @param {ThemeProviderProps} props
 * @param {ReactNode} children
 * @param {string} [defaultTheme="system"] The default theme to apply if no theme is stored in local storage.
 * @param {string} [storageKey="lukla-ui-theme"] The key to use when storing the theme in local storage.
 *
 * @returns {ReactElement}
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'cogknit-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove any previously applied theme classes
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }
    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Returns the current theme and a function to set the theme.
 *
 * Must be used within a ThemeProvider.
 *
 * @returns {object} An object containing the current theme and a function to set the theme.
 *
 * @throws {Error} If used outside of a ThemeProvider.
 */
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
